/**
 * useEnhanceCredits — iter v33 r29
 * ────────────────────────────────────────────────────────────────────────
 * User: «Prezzo Chiaro sul Bottone: sul bottone "Migliora tutte" mostra
 * "≈ 8 crediti" cosi sai in anticipo cosa spenderai».
 *
 * Hook leggero che espone il costo unitario dell'azione richiesta prendendolo
 * dall'endpoint `/api/credits/me` (che restituisce `action_costs` mappa).
 * Se l'utente e' founder o in beta_mode (illimitato), ritorna `isUnlimited=true`
 * e il chiamante nasconde il tag crediti.
 *
 * Caching: la mappa `action_costs` cambia molto raramente (solo admin toggle).
 * Tengo una cache in memoria condivisa cosi' non facciamo 10 chiamate se 3
 * componenti diversi usano l'hook.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

let _cache = null;
let _promise = null;

async function _fetchOnce() {
  if (_cache) return _cache;
  if (_promise) return _promise;
  _promise = (async () => {
    try {
      const { data } = await api.get("/credits/me");
      _cache = {
        actionCosts: data?.action_costs || {},
        isUnlimited: !!(data?.founder || data?.unlimited || data?.beta_mode),
        balance: Number(data?.balance_total ?? 0),
      };
    } catch {
      _cache = { actionCosts: {}, isUnlimited: false, balance: 0 };
    } finally {
      _promise = null;
    }
    return _cache;
  })();
  return _promise;
}

export default function useEnhanceCredits(action = "photo_enhance") {
  const [state, setState] = useState(() =>
    _cache
      ? { cost: _cache.actionCosts[action] ?? 1, isUnlimited: _cache.isUnlimited, balance: _cache.balance, loading: false }
      : { cost: 1, isUnlimited: false, balance: 0, loading: true },
  );

  useEffect(() => {
    let on = true;
    (async () => {
      const d = await _fetchOnce();
      if (!on) return;
      setState({
        cost: d.actionCosts[action] ?? 1,
        isUnlimited: d.isUnlimited,
        balance: d.balance,
        loading: false,
      });
    })();
    return () => { on = false; };
  }, [action]);

  return state;
}

/** Invalida la cache — utile dopo top-up manuale o cambio piano. */
export function invalidateEnhanceCreditsCache() {
  _cache = null;
  _promise = null;
}
